/**
 * Shadow DOM root helper for the POLST widget.
 *
 * Every widget instance renders inside a Shadow DOM (`{ mode: "open" }`)
 * so third-party host CSS cannot reach widget internals and vice versa.
 * This module exposes `createRoot()` — the single entry point mount()
 * uses to build its isolated render target — and owns attaching the
 * inlined stylesheet + theme tokens.
 */

import { applyBrandTheme, applyHostAccentOverride, type BrandThemeDTO } from "./brand-theme";
import { installShadowStyles, normalizeThemeMode, type ThemeMode } from "./theme";

/**
 * Options accepted by `createRoot`. Keep this narrow — modes and
 * renderer config stay in `mount()`.
 */
export interface CreateRootOptions {
  /**
   * Theme resolution mode.
   *   - `"auto"` (default): follow `prefers-color-scheme`.
   *   - `"light"` / `"dark"`: force that palette regardless of OS pref.
   */
  theme?: ThemeMode;
  /**
   * POL-644 — Resolved brand theme returned by the public REST DTO.
   * When supplied, the shadow root applies `--polst-*` overrides to
   * the host element, injects the brand `@font-face`, and appends
   * the sanitised `customCss`. See `brand-theme.ts` for the full
   * precedence chain.
   *
   * Pass `null`/`undefined` to opt out of brand styling — the widget
   * falls back to the `theme.ts` defaults.
   */
  brandTheme?: BrandThemeDTO | null;
}

/**
 * Result bundle: the shadow root for the renderer to write into, plus
 * a teardown function that reverses every side-effect `createRoot`
 * performed on the host element.
 */
export interface ShadowRootBundle {
  root: ShadowRoot;
  /** Remove attached stylesheets and drain the shadow tree. */
  dispose(): void;
}

/**
 * Attach a Shadow DOM to `hostElement` and seed it with the widget's
 * base stylesheet + theme tokens. Idempotent-ish: if the host already
 * has an open shadow root we reuse it rather than throw, so repeated
 * `mount()` calls on the same element are safe. (A host with a
 * `closed` shadow root is unrecoverable — we throw in that case.)
 */
export function createRoot(
  hostElement: Element,
  options: CreateRootOptions = {},
): ShadowRootBundle {
  if (!hostElement || typeof (hostElement as Element).appendChild !== "function") {
    throw new Error("createRoot(): hostElement must be a DOM Element");
  }

  const theme = normalizeThemeMode(options.theme);

  // Writing `data-theme` on the host element is how the stylesheet's
  // `:host([data-theme=...])` selector picks the forced palette. We
  // only set it when the caller opted out of `auto`.
  if (theme === "auto") {
    // Only remove if no host-controlled override already exists.
    // External code may set data-theme on the host and expect it to win.
    if (!hostElement.hasAttribute("data-theme")) {
      hostElement.removeAttribute("data-theme");
    }
  } else {
    hostElement.setAttribute("data-theme", theme);
  }

  const existing = hostElement.shadowRoot;
  let root: ShadowRoot;
  if (existing) {
    // Host already has an open shadow root (from a prior mount that
    // did not dispose). Clear it and reuse the same root — attaching
    // another shadow root would throw.
    while (existing.firstChild) {
      existing.removeChild(existing.firstChild);
    }
    root = existing;
  } else {
    try {
      root = hostElement.attachShadow({ mode: "open" });
    } catch (err) {
      throw new Error(
        `createRoot(): attachShadow failed on <${hostElement.tagName.toLowerCase()}>: ${
          (err as Error).message
        }`,
      );
    }
  }

  const removeStyles = installShadowStyles(root);

  // POL-644 — Apply the brand theme AFTER the shared stylesheet but
  // BEFORE host-page accent overrides so the precedence chain reads
  // cleanly: defaults < brand < host overrides.
  const removeBrandTheme = options.brandTheme
    ? applyBrandTheme(hostElement, root, options.brandTheme, { themeMode: theme })
    : () => {};

  // POL-644 — Re-apply `data-accent` as inline style so it stomps the
  // brand-supplied accent. `data-theme` doesn't need a re-apply because
  // the `:host([data-theme=…])` selectors live in the base stylesheet
  // and beat inline `--polst-bg` etc. via cascade ordering.
  const removeAccentOverride = applyHostAccentOverride(hostElement);

  // Every widget instance gets a single root container inside the
  // shadow tree. Renderers (modes) mount into this element rather than
  // directly into the ShadowRoot so they can freely call replaceChildren
  // without clobbering our `<style>` fallback element.
  const container = document.createElement("div");
  container.setAttribute("data-polst-widget-root", "");
  // Inheriting the reset color/font from :host is fine, but we want a
  // sensible default `background` on the container so modes render
  // against a solid surface by default.
  container.style.background = "var(--polst-bg)";
  container.style.color = "var(--polst-fg)";
  container.style.fontFamily = "var(--polst-font-stack)";
  container.style.borderRadius = "var(--polst-radius)";
  root.appendChild(container);

  return {
    root,
    dispose(): void {
      removeAccentOverride();
      removeBrandTheme();
      removeStyles();
      while (root.firstChild) {
        root.removeChild(root.firstChild);
      }
    },
  };
}
